/**
 * LoadStep — Step 2: Upload load curve or pick from library
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Button, Typography, Alert, Tabs, Tab, CircularProgress,
  List, ListItemButton, ListItemText, ListItemSecondaryAction, Chip,
} from '@mui/material';
import { Upload, FolderOpen, ArrowBack } from '@mui/icons-material';
import { useAuth, vortxColors, vortxTypography } from '@vortx/shared-ui';
import { uploadLoadProfile, listCurves, getCurve, saveCurve } from '../services/partnerService';

function LoadStep({ initialData, onComplete, onBack }) {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0); // 0 = Upload, 1 = Library
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const [curves, setCurves] = useState([]);
  const [loadingCurves, setLoadingCurves] = useState(false);
  const [selectedCurve, setSelectedCurve] = useState(null);

  const fileInputRef = useRef(null);

  // Load library curves when switching to Library tab
  useEffect(() => {
    if (tab === 1 && curves.length === 0) {
      setLoadingCurves(true);
      listCurves(currentUser)
        .then(setCurves)
        .catch((e) => console.warn('Failed to load curves:', e))
        .finally(() => setLoadingCurves(false));
    }
  }, [tab, currentUser, curves.length]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const result = await uploadLoadProfile(currentUser, file);
      setUploadResult({ ...result, source_file: file.name });
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  }, [currentUser]);

  const handleSaveAndContinue = useCallback(async () => {
    if (!uploadResult?.loads_kw) return;

    // Save to library for reuse
    try {
      await saveCurve(currentUser, {
        label: uploadResult.source_file || 'Uploaded Profile',
        loads_kw: uploadResult.loads_kw,
        source_file: uploadResult.source_file,
      });
    } catch (e) {
      console.warn('Failed to save curve to library (non-fatal):', e);
    }

    onComplete({
      loads_kw: uploadResult.loads_kw,
      source_file: uploadResult.source_file,
    });
  }, [uploadResult, currentUser, onComplete]);

  const handleContinueWithUpload = useCallback(() => {
    if (!uploadResult?.loads_kw) return;
    onComplete({
      loads_kw: uploadResult.loads_kw,
      source_file: uploadResult.source_file,
    });
  }, [uploadResult, onComplete]);

  const handleSelectCurve = useCallback(async (curve) => {
    setSelectedCurve(curve);
    // Fetch full 8760 data
    try {
      const fullCurve = await getCurve(currentUser, curve.id);
      onComplete({
        loads_kw: fullCurve.loads_kw,
        curve_id: curve.id,
        source_file: fullCurve.source_file,
      });
    } catch (e) {
      setUploadError(`Failed to load curve: ${e.message}`);
    }
  }, [currentUser, onComplete]);

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, mb: 3 }}
      >
        Load Profile
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Upload />} label="Upload New" iconPosition="start" />
        <Tab icon={<FolderOpen />} label="From Library" iconPosition="start" />
      </Tabs>

      {/* Upload Tab */}
      {tab === 0 && (
        <Box>
          <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.85rem', opacity: 0.7, mb: 2 }}>
            Upload a CSV file with hourly load data (kW). The system will auto-detect columns,
            select the best year, and generate an 8760 hourly profile.
          </Typography>

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <Upload />}
            sx={{
              borderColor: vortxColors.gold,
              color: vortxColors.gold,
              fontFamily: vortxTypography.body,
              fontWeight: 600,
              mb: 2,
              '&:hover': { borderColor: vortxColors.gold, bgcolor: `${vortxColors.gold}10` },
            }}
          >
            {uploading ? 'Processing...' : 'Choose CSV File'}
          </Button>

          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}

          {uploadResult && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Processed {uploadResult.metrics?.original_data_points?.toLocaleString()} data points
                into 8760 hours (year {uploadResult.year})
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Peak: ${Math.round(Math.max(...uploadResult.loads_kw))} kW`} />
                <Chip label={`Annual: ${Math.round(uploadResult.loads_kw.reduce((a, b) => a + b, 0)).toLocaleString()} kWh`} />
                <Chip label={`File: ${uploadResult.source_file}`} variant="outlined" />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveAndContinue}
                  sx={{
                    bgcolor: vortxColors.gold,
                    fontWeight: 600,
                    '&:hover': { bgcolor: vortxColors.darkGold || '#9a7a40' },
                  }}
                >
                  Save to Library & Continue
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleContinueWithUpload}
                  sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                >
                  Continue Without Saving
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Library Tab */}
      {tab === 1 && (
        <Box>
          {loadingCurves ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={20} />
              <Typography>Loading saved curves...</Typography>
            </Box>
          ) : curves.length === 0 ? (
            <Alert severity="info">
              No saved load curves yet. Upload a CSV first, and it will be saved to your library for reuse.
            </Alert>
          ) : (
            <List>
              {curves.map((curve) => (
                <ListItemButton
                  key={curve.id}
                  onClick={() => handleSelectCurve(curve)}
                  selected={selectedCurve?.id === curve.id}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    border: `1px solid ${vortxColors.charcoal}10`,
                  }}
                >
                  <ListItemText
                    primary={curve.label}
                    secondary={`Peak: ${Math.round(curve.peak_kw)} kW · Annual: ${Math.round(curve.annual_kwh).toLocaleString()} kWh`}
                    primaryTypographyProps={{ fontFamily: vortxTypography.body, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontFamily: vortxTypography.body, fontSize: '0.8rem' }}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={`LF: ${(curve.load_factor * 100).toFixed(1)}%`}
                      size="small"
                      variant="outlined"
                    />
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Back button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{ color: vortxColors.charcoal, fontFamily: vortxTypography.body }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
}

export default LoadStep;
